import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { columnFiltersToQueryParams, pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useMerchantSalesAgents } from "@/features/merchant/api/use-merchant-sales-agents";
import { SalesAgentFormDialog } from "@/features/merchant/components/merchant-sales-agents/sales-agent-form-dialog";
import { SalesAgentRowActions } from "@/features/merchant/components/merchant-sales-agents/sales-agent-row-actions";
import {
  salesAgentsColumnsFactory,
  type SalesAgentRow,
} from "@/features/merchant/components/merchant-sales-agents/sales-agents-columns";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

/** Section « Agents de vente » du détail marchand. */
export function SalesAgentsSection({ merchantId }: { merchantId: string }) {
  const { t: tm } = useMerchantTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: columnFiltersToQueryParams(columnFilters),
    }),
    [pagination, columnFilters]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useMerchantSalesAgents(merchantId, params);

  const rows = useMemo(
    () => (data?.items ?? []).filter((a): a is SalesAgentRow => Boolean(a.id)),
    [data]
  );

  const columns = useMemo(
    () =>
      salesAgentsColumnsFactory((agent) => (
        <SalesAgentRowActions agent={agent} merchantId={merchantId} />
      )),
    [merchantId]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          {tm("merchant.salesAgents.title")}
        </h2>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tm("merchant.salesAgents.create")}
        </Button>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={columns}
        title={tm("merchant.salesAgents.title")}
        exportFileName="merchant-sales-agents"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <SalesAgentFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        merchantId={merchantId}
      />
    </section>
  );
}
